import React from 'react';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';

import { CenteredContainer, Title, Text, Link } from '@nerdwallet/currency';

import Hero from '~/client/components/employee-branding/hero/hero';
import ThreeIconLayout from '~/client/components/employee-branding/three-icon-layout/three-icon-layout';
import MiniHero from '~/client/components/employee-branding/mini-hero/mini-hero';
import BoxTable from '~/client/components/employee-branding/box-table/box-table';
import BoxText from '~/client/components/employee-branding/box-text/box-text';
import CsrBox from '~/client/components/employee-branding/csr-box/csr-box';

import ZigZag from '~/client/components/employee-branding/zig-zag/zig-zag';
import LogoSalad from '~/client/components/press-page/press-logo-salad/press-logo-salad';

import ThreeIconsLayoutContent from '~/client/lib/employee-branding/three-icon-layout-tiles';
import BoxTableData from '~/client/lib/employee-branding/box-table-data';
import BoxTableNarrowData from '~/client/lib/employee-branding/box-table-narrow-data';
import BoxTextData from '~/client/lib/employee-branding/box-text-data';
import LogoTiles from '~/client/lib/employee-branding/csr-logos';

const HERO_COPY = [
  {
    header: '',
    copy: (
      <React.Fragment>
        Want to help by depositing some of your company&rsquo;s banked dollars?{' '}
        Fill out our form below to receive a list of approved credit unions near{' '}
        you from Inclusiv. If you have any additional questions, email us at{' '}
        <Link
          href="mailto:socialimpact@nerdwallet.com"
          color="white"
          className={classNames([classes('underline')])}
        >
          socialimpact@nerdwallet.com
        </Link>
      </React.Fragment>
    ),
  },
];

const FINANCIAL_EQUALITY_COPY = (
  <React.Fragment>
    NerdWallet is taking a stand against financial inequality. Through our first
    social impact initiative, we&rsquo;re supporting the work of credit unions{' '}
    serving low income communities to help provide access to equitable financial
    products and services.
    <br />
    <br />
    <strong>Ready to make a difference now?</strong>
    <br />
    If you’re an employee passionate about your company getting involved,{' '}
    <Link
      href="/corporate-social-responsibility/email"
      color="white"
      className={classNames([classes('underline')])}
    >
      download this email
    </Link>{' '}
    to send to your leadership team.
    <br />
    <br />
    If you’re ready to help the cause now,{' '}
    <Link
      href="https://forms.gle/HVGcnTMYwW9Bo95n8"
      color="white"
      className={classNames([classes('underline')])}
    >
      fill out this form.
    </Link>
    <br />
    <br />
    For more on the issue, keep reading.
  </React.Fragment>
);

const CSR_INSERT_COPY = (
  <React.Fragment>
    We&rsquo;re asking companies across the country to join us by depositing $1
    million of their banked dollars into these credit unions, putting their
    money to work, and helping us turn this socially conscious money move into a
    movement.
    <br />
    <br />
    For next steps,{' '}
    <Link
      href="https://forms.gle/HVGcnTMYwW9Bo95n8"
      color="white"
      className={classNames([classes('underline')])}
    >
      fill out our form
    </Link>
    . We’ll connect you with Inclusiv to find a credit union accepting deposits
    near you.
  </React.Fragment>
);

const SocialImpactPage = () => (
  <div>
    <div
      className={classNames([
        classes('margin-bottom-4'),
        classes('margin-bottom-5--wide'),
      ])}
    >
      <Hero
        backgroundColor="#02427E"
        color="#EEEEEE"
        title="The Financial Equality Project"
        copy={FINANCIAL_EQUALITY_COPY}
        imageUrlMobile="https://www.nerdwallet.com/cdn/company/2021/hero/CSR2.0-Hero-Mobile.png"
        imageUrlDesktop="https://www.nerdwallet.com/cdn/company/2021/hero/CSR2.0-Hero-Desktop.png"
      />
    </div>
    <CenteredContainer>
      <Title
        size="small"
        className={classNames([
          classes('margin-horizontal-auto'),
          classes('margin-bottom-3'),
          classes('text-align-left'),
          classes('margin-bottom-4--wide'),
          classes('text-align-center--wide'),
        ])}
        component="h2"
      >
        Financial inequality is a reality for millions of Americans.
      </Title>
    </CenteredContainer>
    <ThreeIconLayout
      {...ThreeIconsLayoutContent}
      className={classNames([
        classes('margin-bottom-3-half'),
        classes('margin-bottom-4--wide'),
      ])}
    />
    <CenteredContainer
      className={classNames([
        classes('margin-bottom-3-half'),
        classes('margin-bottom-5--wide'),
      ])}
    >
      <div
        className={classNames([
          classes('margin-horizontal-auto'),
          classes('text-align-center'),
        ])}
      >
        <Text>
          <sup>*</sup>Source:{' '}
        </Text>
        <Link href="https://www.federalreserve.gov/publications/2019-economic-well-being-of-us-households-in-2018-banking-and-credit.htm">
          federalreserve.gov
        </Link>
      </div>
      <div
        className={classNames([
          classes('margin-horizontal-auto'),
          classes('margin-bottom-3-half'),
          classes('text-align-center'),
          classes('margin-bottom-5--wide'),
        ])}
      >
        <Text>
          <sup>**</sup>Source:{' '}
        </Text>
        <Link href="https://s3.amazonaws.com/cfsi-innovation-files-2018/wp-content/uploads/2017/02/27001759/2016-Financially-Underserved-Market-Size-Study_Center-for-Financial-Services-Innovation.pdf">
          CFSI 2016 Financially Underserved Market Size Study 2016
        </Link>
      </div>
      <MiniHero
        backgroundColor="#F2EDE6"
        backgroundImageUrl="https://www.nerdwallet.com/cdn/company/2021/csr/CSR2.0_bg-v2.jpg"
        color="#000000"
        copy="Credit unions serving low-income communities are vital to bringing economic equality to everyone"
      />
      <div
        className={classNames([
          classes('margin-horizontal-auto'),
          classes('text-align-center--wide'),
          classes('width-80--wide'),
          classes('margin-vertical-3-half'),
          classes('margin-vertical-5--wide'),
        ])}
      >
        <Text>
          These credit unions are a safe alternative to predatory financial
          options (like payday loans). They deeply understand their members’
          greatest needs and help by offering no to low minimum balance
          accounts, financial counseling, affordable access to credit, as well
          as a safe place to save and build assets.
        </Text>
      </div>
      <BoxTable {...BoxTableData} />
    </CenteredContainer>
    <div
      className={classNames([
        classes('margin-vertical-3-half'),
        classes('margin-vertical-5--wide'),
      ])}
    >
      <MiniHero
        isDefault
        backgroundImageUrl="https://www.nerdwallet.com/cdn/company/2021/csr/images/CSR_LPC_image-tout_675x1824_2x.jpg"
        color="#ffffff"
        copy="NerdWallet is championing the effort to create real change in financially underserved neighborhoods"
      />
    </div>
    <CenteredContainer
      className={classNames([
        classes('margin-bottom-3-half'),
        classes('margin-bottom-5--wide'),
      ])}
    >
      <CsrBox
        insertBgColor="#005FB9"
        insertBgImage="https://www.nerdwallet.com/cdn/company/2021/csr/CSR2.0_bg-02-v2.jpg"
        insertColor="white"
        title="Creating a movement to advance financial equality"
        copy="Depositing bankable cash into credit unions serving low-income communities helps provide access to equitable financial products and services. NerdWallet has deposited $2 million in bankable dollars with Self Help Federal Credit Union to fund personal, mortgage, and commercial loans for its members.<br /><br />In partnership with Inclusiv, the nonprofit network of community development credit unions (CDCUs) providing capital and support for hundreds of credit unions serving low income communities we are making it easy for companies to find the right credit union in need of capital."
        insertTitle="Make a difference.<br />Make a deposit."
        insertCopy={CSR_INSERT_COPY}
        insertImage="https://www.nerdwallet.com/cdn/company/2021/csr/images/demand-action.png"
      />
      <BoxText {...BoxTextData} />
    </CenteredContainer>
    <div
      className={classNames([
        classes('margin-bottom-3-half'),
        classes('margin-bottom-5--wide'),
      ])}
    >
      <LogoSalad
        {...LogoTiles}
        alignItems="flex-end"
        backgroundColor="#F2EDE6"
        header="Partners who’ve joined the cause"
        copy="We may have started the movement, but we’re not alone. Your organization could join these partners committed to making a difference in financial equality."
        topMargin={false}
      />
      <ZigZag
        backgroundColor="#006642"
        color="white"
        copyArray={HERO_COPY}
        imageUrlDesktop="https://www.nerdwallet.com/cdn/company/2021/csr/CSR2.0_final-cta-img_desktop.png"
        imageUrlMobile="https://www.nerdwallet.com/cdn/company/2021/csr/CSR2.0_final-cta-img_desktop.png"
        imageAlt=""
        title="Now is the time to help"
        ctaText="contact us"
        ctaUrl="https://forms.gle/HVGcnTMYwW9Bo95n8"
        ctaPrimary={false}
        imageFirst
        titleFirst
      />
    </div>
    <CenteredContainer
      className={classNames([
        classes('margin-bottom-3-half'),
        classes('margin-bottom-5--wide'),
      ])}
    >
      <Title
        size="small"
        className={classNames([
          classes('margin-horizontal-auto'),
          classes('margin-bottom-3-half'),
          classes('text-align-left'),
          classes('margin-bottom-5--wide'),
          classes('text-align-center--wide'),
        ])}
        component="h2"
      >
        Looking to learn more about this issue?
      </Title>
      <BoxTable {...BoxTableNarrowData} />
    </CenteredContainer>
  </div>
);

SocialImpactPage.displayName = 'Social Impact';

export default SocialImpactPage;
